import React, { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { Outlet, useNavigate, useLocation, Link } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { Dropdown } from 'react-bootstrap';
import {
  BiGridAlt,
  BiBuilding,
  BiGroup,
  BiShield,
  BiGrid,
  BiCog,
  BiClipboard,
  BiHome,
  BiFolder,
  BiBook,
  BiUser,
  BiTime,
  BiCheckCircle,
  BiBarChart,
  BiCollection,
  BiBell,
  BiHelpCircle,
  BiLogOut,
  BiUserCircle,
  BiChevronDown,
  BiMenu,
  BiX
} from 'react-icons/bi';
import BrandLogo from '../components/brand/BrandLogo';
import { workspaceUserInitials } from '../components/workspace/WorkspaceModal';
import { clearAuth, logout } from '../store/slices/authSlice';
import { clearWorkspaceAccess } from '../hooks/useWorkspaceAccess';
import { platformAPI } from '../services/platformAPI';
import { canManageMasters, isWorkspaceAdmin, userHasAnyPermission, userHasPermission } from '../utils/permissions';
import { APPROVAL_ACCESS, KNOWLEDGE_ACCESS, REGISTER_ACCESS, SECURITIES_ACCESS, SETTINGS_ACCESS, TIMESHEET_ACCESS } from '../config/workspaceAccess';
import { useWorkspaceAccess } from '../hooks/useWorkspaceAccess';
import { documentTitle, TENDER_APP_NAME, WORKSPACE_LABEL } from '../config/branding';
import { openWorkspaceHelp } from '../utils/openWorkspaceHelp';
import { setWorkspaceDateFormat } from '../utils/workspaceDates';
import { applyBrandColours } from '../utils/brandColours';
import WorkspaceHeaderSearch from '../components/workspace/WorkspaceHeaderSearch';
import WorkspaceBreadcrumb from '../components/workspace/WorkspaceBreadcrumb';
import { recordWorkspaceVisit } from '../utils/workspaceRecent';
import '../styles/workspace-theme.scss';

const NOTICE_LABELS = {
  ELIGIBILITY_REVIEW: 'Eligibility',
  TECHNICAL_RESPONSE_REVIEW: 'Response',
  COMMERCIAL_APPROVAL: 'Costing',
  BID_DECISION: 'Decision',
  TIMESHEET_APPROVAL: 'Timesheet',
  DOCUMENT_REVIEW: 'Document',
  CREDENTIAL_EXPIRY: 'Credential',
  DEADLINE_REMINDER: 'Deadline',
  SECURITY_EXPIRY: 'Security',
  SECURITY_CLAIM: 'Security',
  SECURITY_RELEASE: 'Security',
  PREBID_QUERY: 'Query',
  CORRIGENDUM: 'Corrigendum',
  CORRIGENDUM_DEADLINE: 'Corrigendum',
  SCORE_APPROVAL: 'Score',
  GENERAL_TASK: 'Task'
};

const NOTICE_STATUS = {
  PENDING: 'Waiting',
  IN_PROGRESS: 'In progress',
  OVERDUE: 'Overdue'
};

function noticeWhen(item) {
  const raw = item?.dueDate || item?.updatedAt;
  if (!raw) return '';
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}

function openNotices(summary) {
  const open = new Set(['PENDING', 'IN_PROGRESS', 'OVERDUE']);
  return (summary?.recentActivity || []).filter((item) => open.has(item.status)).slice(0, 4);
}

function formatRoleLabel(role) {
  if (!role) return '';
  const text = String(role).trim();
  if (!text) return '';
  return text
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

const PLATFORM_NAV = [
  { path: '/workspace', label: WORKSPACE_LABEL, icon: BiGridAlt, permission: null },
  { path: '/platform/company', label: 'Company setup', icon: BiBuilding, anyOf: ['platform.company.manage', 'company.view'] },
  { path: '/platform/users', label: 'Users', icon: BiGroup, anyOf: ['platform.users.manage', 'users.view'] },
  { path: '/platform/roles', label: 'Roles & access', icon: BiShield, anyOf: ['platform.roles.manage', 'roles.view'] },
  { path: '/platform/applications', label: 'Applications', icon: BiGrid, anyOf: ['platform.applications.manage', 'applications.view'] },
  { path: '/platform/settings', label: 'Settings', icon: BiCog, anyOf: ['platform.ai.configure', 'ai.view'] },
  { path: '/platform/audit', label: 'Audit trail', icon: BiClipboard, anyOf: ['platform.audit.read', 'audit.view'] }
];

const TENDER_NAV = [
  { path: '/apps/tender', label: 'Overview', icon: BiHome, anyOf: ['tender.read', 'tenders.view'] },
  { path: '/apps/tender/register', label: 'Tender register', icon: BiFolder, anyOf: REGISTER_ACCESS },
  { path: '/apps/tender/knowledge', label: 'Knowledge bank', icon: BiBook, anyOf: KNOWLEDGE_ACCESS },
  { path: '/apps/tender/personnel', label: 'Personnel', icon: BiUser, anyOf: ['tender.read', 'personnel.view'] },
  { path: '/apps/tender/timesheets', label: 'Timesheets', icon: BiTime, anyOf: TIMESHEET_ACCESS },
  { path: '/apps/tender/securities', label: 'BG / EMD', icon: BiShield, anyOf: SECURITIES_ACCESS },
  { path: '/apps/tender/approvals', label: 'Approvals', icon: BiCheckCircle, anyOf: APPROVAL_ACCESS },
  { path: '/apps/tender/reports', label: 'Reports', icon: BiBarChart, dividerBefore: true, anyOf: ['tender.reports.read', 'reports.view'] }
];

function navItemAllowed(item, permissions) {
  if (item.anyOf?.length) return userHasAnyPermission(permissions, item.anyOf);
  if (item.permission) return userHasPermission(permissions, item.permission);
  return true;
}

const PlatformShell = ({ mode = 'platform' }) => {
  const { user } = useSelector((state) => state.auth);
  const access = useWorkspaceAccess();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [bootstrap, setBootstrap] = useState(null);
  const [navOpen, setNavOpen] = useState(false);
  const [workSummary, setWorkSummary] = useState({ pendingWork: 0, overdueWork: 0 });

  useLayoutEffect(() => {
    const name = bootstrap?.company?.displayName;
    const page = mode === 'tender' ? TENDER_APP_NAME : WORKSPACE_LABEL;
    document.title = name ? `${name} | ${page}` : documentTitle(page);
  }, [mode, bootstrap]);

  useEffect(() => {
    document.body.classList.add('workspace-theme');
    Promise.all([
      platformAPI.bootstrap(),
      platformAPI.workspaceSummary().catch(() => ({ data: { data: {} } }))
    ]).then(([bootRes, sumRes]) => {
      const next = bootRes.data?.data;
      setWorkspaceDateFormat(next?.company?.dateFormat);
      setBootstrap(next);
      setWorkSummary(sumRes.data?.data || {});
    }).catch(() => {});
    return () => document.body.classList.remove('workspace-theme');
  }, []);

  useEffect(() => {
    applyBrandColours(bootstrap?.branding?.primaryColor, bootstrap?.branding?.secondaryColor);
  }, [bootstrap]);

  const permissions = access.ready ? access.permissions : (bootstrap?.permissions || user?.permissions || []);
  const notificationCount = (workSummary?.pendingWork ?? 0) + (workSummary?.overdueWork ?? 0);

  const navItems = useMemo(() => {
    const source = mode === 'tender' ? TENDER_NAV : PLATFORM_NAV;
    return source.filter((item) => navItemAllowed(item, permissions));
  }, [mode, permissions]);

  const mastersNav = useMemo(() => {
    const roles = user?.roles || bootstrap?.user?.roles || [];
    if (!canManageMasters(permissions, roles)) return [];
    return [{ path: '/apps/tender/masters', label: 'Masters', icon: BiCollection }];
  }, [permissions, user?.roles, bootstrap?.user?.roles]);

  const handleLogout = async () => {
    sessionStorage.setItem('sessionEnded', '1');
    try {
      await dispatch(logout()).unwrap();
    } catch {
      dispatch(clearAuth());
    }
    clearWorkspaceAccess();
    window.location.replace('/login');
  };

  const companyLabel = bootstrap?.company?.displayName || user?.company?.displayName || 'Dhruv Consultancy';
  const companyCode = bootstrap?.company?.code;
  const roles = user?.roles || bootstrap?.user?.roles || [];
  const canSettings = isWorkspaceAdmin(roles) || userHasAnyPermission(permissions, SETTINGS_ACCESS);
  const activeNavPath = useMemo(() => {
    const pathname = location.pathname;
    const accountPaths = ['/apps/tender/support', '/apps/tender/my-work', '/apps/tender/settings', '/apps/tender/profile'];
    if (accountPaths.some((path) => pathname === path || pathname.startsWith(`${path}/`))) {
      return null;
    }
    const paths = [...navItems, ...mastersNav].map((item) => item.path).sort((a, b) => b.length - a.length);
    for (const path of paths) {
      if (pathname === path || pathname.startsWith(`${path}/`)) {
        return path;
      }
    }
    return null;
  }, [location.pathname, navItems, mastersNav]);

  const isHubHome = mode === 'platform' && location.pathname === '/workspace';

  const primaryRoleLabel = useMemo(() => {
    const roles = user?.roles || bootstrap?.user?.roles || [];
    return formatRoleLabel(roles[0]);
  }, [user?.roles, bootstrap?.user?.roles]);

  const adminPath = useMemo(() => {
    if (userHasAnyPermission(permissions, ['platform.users.manage', 'users.view'])) return '/platform/users';
    if (userHasAnyPermission(permissions, ['platform.company.manage', 'company.view'])) return '/platform/company';
    if (userHasAnyPermission(permissions, ['platform.roles.manage', 'roles.view'])) return '/platform/roles';
    if (userHasAnyPermission(permissions, ['platform.applications.manage', 'applications.view'])) return '/platform/applications';
    if (userHasAnyPermission(permissions, ['platform.audit.read', 'audit.view'])) return '/platform/audit';
    if (userHasAnyPermission(permissions, ['platform.ai.configure', 'ai.view'])) return '/platform/settings';
    return '/platform/company';
  }, [permissions]);

  const footerNav = useMemo(() => {
    const inTender = mode === 'tender';
    return [
      { path: '/workspace', label: 'Applications', icon: BiGridAlt },
      ...mastersNav,
      ...(canSettings ? [{ path: inTender ? '/apps/tender/settings' : '/platform/settings', label: 'Settings', icon: BiCog }] : []),
      { path: inTender ? '/apps/tender/support' : '/platform/support', label: 'Support', icon: BiHelpCircle }
    ];
  }, [mode, mastersNav, canSettings]);

  const canAdminister = useMemo(
    () =>
      userHasAnyPermission(permissions, [
        'platform.company.manage', 'company.view',
        'platform.users.manage', 'users.view',
        'platform.roles.manage', 'roles.view',
        'platform.applications.manage', 'applications.view',
        'platform.audit.read', 'audit.view',
        'platform.ai.configure', 'ai.view'
      ]),
    [permissions]
  );

  useEffect(() => {
    if (user?._id || user?.id) {
      recordWorkspaceVisit(user._id || user.id, location.pathname);
    }
  }, [location.pathname, user?._id, user?.id]);

  useEffect(() => {
    setNavOpen(false);
  }, [location.pathname]);

  const brandBlock = (
    <div className="workspace-shell-brand workspace-shell-brand--logo">
      <BrandLogo
        variant="emblem-shell"
        className="workspace-shell-brand__logo"
        alt={mode === 'tender' ? TENDER_APP_NAME : companyLabel}
        src={bootstrap?.branding?.logoUrl || ''}
      />
    </div>
  );

  const pageTitle = mode === 'tender' ? TENDER_APP_NAME : WORKSPACE_LABEL;

  const searchPages = useMemo(() => {
    const rows = [
      ...navItems.map((item) => ({ path: item.path, label: item.label })),
      ...mastersNav.map((item) => ({ path: item.path, label: item.label })),
      ...footerNav.map((item) => ({ path: item.path, label: item.label })),
      { path: mode === 'tender' ? '/apps/tender/my-work' : '/my-work', label: 'My work' },
      { path: '/workspace', label: WORKSPACE_LABEL },
      { path: '/apps/tender', label: TENDER_APP_NAME },
      { path: '/profile', label: 'Profile' }
    ];
    const seen = new Set();
    return rows.filter((row) => {
      if (!row.path || seen.has(row.path)) return false;
      seen.add(row.path);
      return true;
    });
  }, [navItems, mastersNav, footerNav, mode]);

  const userAccountMenu = (
    <Dropdown align="end" className="workspace-topbar__user">
      <Dropdown.Toggle
        variant="link"
        className={`workspace-topbar__user-toggle ${isHubHome ? 'workspace-hub-header__user-toggle' : 'workspace-topbar__user-toggle--icon'}`}
        id="workspace-user-menu"
        aria-label="Account menu"
      >
        {isHubHome ? (
          <>
            <span className="workspace-hub-header__avatar" aria-hidden>
              {workspaceUserInitials(user?.name)}
            </span>
            <span className="workspace-hub-header__user-text">
              <span className="workspace-hub-header__user-name">{user?.name || 'Account'}</span>
              {primaryRoleLabel ? (
                <span className="workspace-hub-header__user-role">{primaryRoleLabel}</span>
              ) : null}
            </span>
            <BiChevronDown className="workspace-hub-header__chevron" aria-hidden />
          </>
        ) : (
          <BiUser size={22} />
        )}
      </Dropdown.Toggle>
      <Dropdown.Menu className="workspace-topbar__menu workspace-topbar__user-menu" popperConfig={{ strategy: 'fixed' }}>
        <div className="workspace-topbar__user-hero">
          <span className="workspace-topbar__avatar workspace-topbar__avatar--lg" aria-hidden>
            {workspaceUserInitials(user?.name)}
          </span>
          <div className="workspace-topbar__user-hero-text">
            <div className="workspace-topbar__user-card-name">{user?.name || 'Account'}</div>
            {companyCode ? (
              <div className="workspace-topbar__user-card-meta">Company · {companyCode}</div>
            ) : null}
            <div className="workspace-topbar__user-card-email" title={user?.email}>
              {user?.email}
            </div>
          </div>
        </div>
        <div className="workspace-topbar__user-menu-body">
          <Dropdown.Item as={Link} to={mode === 'tender' ? '/apps/tender/profile' : '/profile'} className="workspace-topbar__menu-item">
            <span className="workspace-topbar__menu-icon" aria-hidden><BiUserCircle /></span>
            My profile
          </Dropdown.Item>
          {canSettings ? (
            <Dropdown.Item as={Link} to={mode === 'tender' ? '/apps/tender/settings' : '/platform/settings'} className="workspace-topbar__menu-item">
              <span className="workspace-topbar__menu-icon" aria-hidden><BiCog /></span>
              Settings
            </Dropdown.Item>
          ) : null}
          <Dropdown.Item as={Link} to={mode === 'tender' ? '/apps/tender/my-work' : '/my-work'} className="workspace-topbar__menu-item">
            <span className="workspace-topbar__menu-icon" aria-hidden><BiGridAlt /></span>
            My work
          </Dropdown.Item>
        </div>
        <div className="workspace-topbar__user-menu-footer">
          <Dropdown.Item as="button" type="button" onClick={handleLogout} className="workspace-topbar__menu-item workspace-topbar__menu-item--signout">
            <span className="workspace-topbar__menu-icon" aria-hidden><BiLogOut /></span>
            Sign out
          </Dropdown.Item>
        </div>
      </Dropdown.Menu>
    </Dropdown>
  );

  const workPath = mode === 'tender' ? '/apps/tender/my-work' : '/my-work';
  const pendingCount = workSummary?.pendingWork ?? 0;
  const overdueCount = workSummary?.overdueWork ?? 0;
  const notices = openNotices(workSummary);
  const notificationMenu = (
    <Dropdown align="end" className="workspace-topbar__notif">
      <Dropdown.Toggle variant="link" className="workspace-topbar__icon-btn" id="workspace-notifications" aria-label="Notifications">
        <BiBell size={22} />
        {notificationCount > 0 ? (
          <span className="workspace-topbar__badge">{notificationCount > 99 ? '99+' : notificationCount}</span>
        ) : null}
      </Dropdown.Toggle>
      <Dropdown.Menu className="workspace-topbar__menu workspace-topbar__notice" popperConfig={{ strategy: 'fixed' }}>
        <div className="notice-head">
          <p className="notice-kicker">Your desk</p>
          <div className="notice-title">
            <h2>Notifications</h2>
            <span>{notificationCount === 1 ? '1 open' : `${notificationCount} open`}</span>
          </div>
        </div>
        <div className="notice-facts">
          <Dropdown.Item as={Link} to={workPath} className="notice-fact">
            <strong>{pendingCount}</strong>
            <span>Pending</span>
          </Dropdown.Item>
          <Dropdown.Item as={Link} to={workPath} className={`notice-fact${overdueCount > 0 ? ' is-overdue' : ''}`}>
            <strong>{overdueCount}</strong>
            <span>Overdue</span>
          </Dropdown.Item>
        </div>
        <div className="notice-list">
          {notices.length ? notices.map((item) => (
            <Dropdown.Item
              key={item._id}
              as={Link}
              to={item.routePath || workPath}
              className={`notice-item is-${String(item.status || '').toLowerCase()}`}
            >
              <span className="notice-item__copy">
                <strong>{item.title}</strong>
                <small>
                  {NOTICE_LABELS[item.type] || 'Work'}
                  {' · '}
                  {NOTICE_STATUS[item.status] || 'Open'}
                  {noticeWhen(item) ? ` · ${noticeWhen(item)}` : ''}
                </small>
              </span>
            </Dropdown.Item>
          )) : (
            <p className="notice-empty">Nothing is waiting on you.</p>
          )}
        </div>
        <div className="notice-foot">
          <Dropdown.Item as={Link} to={workPath} className="notice-link">My work</Dropdown.Item>
          {mode === 'tender' ? (
            <Dropdown.Item as={Link} to="/apps/tender/approvals" className="notice-link">Approval queue</Dropdown.Item>
          ) : null}
        </div>
      </Dropdown.Menu>
    </Dropdown>
  );

  const menuToggle = (
    <button
      type="button"
      className="workspace-nav-toggle"
      aria-expanded={navOpen}
      aria-controls={isHubHome ? 'workspace-hub-nav' : 'workspace-sidebar'}
      aria-label={navOpen ? 'Close menu' : 'Open menu'}
      onClick={() => setNavOpen((open) => !open)}
    >
      {navOpen ? <BiX size={24} /> : <BiMenu size={24} />}
    </button>
  );

  const topbarActions = (
    <div className="workspace-topbar__actions">
      {notificationMenu}
      {!isHubHome ? (
        <button
          type="button"
          className="workspace-topbar__icon-btn"
          aria-label="Open help guide in new window"
          title="Help guide"
          onClick={() => openWorkspaceHelp(mode === 'tender' ? 'tender' : 'platform')}
        >
          <BiHelpCircle size={22} />
        </button>
      ) : null}
      {userAccountMenu}
    </div>
  );

  return (
    <div className={`workspace-shell ${isHubHome ? 'workspace-shell--hub' : ''} ${navOpen ? 'is-nav-open' : ''}`}>
      {navOpen ? (
        <button
          type="button"
          className="workspace-nav-backdrop"
          aria-label="Close menu"
          onClick={() => setNavOpen(false)}
        />
      ) : null}
      {isHubHome ? (
        <header className="workspace-hub-header workspace-shell-header">
          {menuToggle}
          <div className="workspace-hub-header__brand">
            <BrandLogo variant="emblem-shell" alt={companyLabel} />
            <span className="workspace-hub-header__company">{companyLabel}</span>
          </div>
          <nav id="workspace-hub-nav" className="workspace-hub-header__nav" aria-label="Workspace navigation">
            <Link
              to="/workspace"
              className={`workspace-hub-header__nav-link ${location.pathname === '/workspace' ? 'is-active' : ''}`}
            >
              {WORKSPACE_LABEL}
            </Link>
            <Link
              to="/my-work"
              className={`workspace-hub-header__nav-link ${location.pathname.startsWith('/my-work') ? 'is-active' : ''}`}
            >
              My Work
            </Link>
            {canAdminister ? (
              <Link
                to={adminPath}
                className={`workspace-hub-header__nav-link ${location.pathname.startsWith('/platform') ? 'is-active' : ''}`}
              >
                Administration
              </Link>
            ) : null}
          </nav>
          <div className="workspace-hub-header__actions">{topbarActions}</div>
        </header>
      ) : (
        <>
          <div className="workspace-sidebar__brand workspace-shell-header workspace-shell__brand" aria-label="Product branding">
            {brandBlock}
          </div>
          <header className="workspace-topbar workspace-shell-header workspace-shell__topbar">
            {menuToggle}
            <div className="workspace-topbar__left">
              <h1 className="workspace-topbar__title">{companyLabel}</h1>
              <span className="workspace-topbar__context">{pageTitle}</span>
            </div>
            <WorkspaceHeaderSearch pages={searchPages} />
            {topbarActions}
          </header>
          <aside id="workspace-sidebar" className="workspace-sidebar workspace-shell__sidebar">
            <nav className="workspace-sidebar__nav" aria-label="Main navigation">
              {navItems.map((item) => {
                const Icon = item.icon;
                const active = activeNavPath === item.path;
                return (
                  <div
                    key={item.path}
                    className={`nav-item${item.dividerBefore ? ' nav-item--ruled' : ''}${active ? ' active' : ''}`}
                    onClick={() => navigate(item.path)}
                    onKeyDown={(e) => e.key === 'Enter' && navigate(item.path)}
                    role="button"
                    tabIndex={0}
                  >
                    <Icon className="nav-item__icon" size={20} aria-hidden />
                    <span className="nav-item__label">{item.label}</span>
                  </div>
                );
              })}
            </nav>
            <nav
              className="workspace-sidebar__footer"
              aria-label="Sidebar shortcuts"
              style={{ '--ws-footer-cols': Math.max(footerNav.length, 1) }}
            >
              {footerNav.map((item) => {
                const Icon = item.icon;
                const active = location.pathname === item.path || (item.path !== '/workspace' && location.pathname.startsWith(`${item.path}/`));
                return (
                  <button
                    key={item.path}
                    type="button"
                    className={`workspace-sidebar__footer-btn${active ? ' is-active' : ''}`}
                    onClick={() => navigate(item.path)}
                  >
                    <Icon aria-hidden />
                    <span>{item.label}</span>
                  </button>
                );
              })}
            </nav>
          </aside>
        </>
      )}
      <div className="workspace-main workspace-shell__main">
        <main className="workspace-content">
          <div className="workspace-content__inner">
            <WorkspaceBreadcrumb />
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
};

export default PlatformShell;
